import {Trans} from '@lingui/macro';
import {isEqual} from 'lodash';
import React, {FC, useMemo} from 'react';

import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {TooltipIconWithHover} from '../../../../analytics/components/reportElements/tooltips/analyticsTooltips';
import {getAtomDefaultParameters, getParameters} from '../../../editors/expressions/expressionInferHelpers';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import {AtomCardProps} from './atomCard';
import {AtomIcon} from './atomIcon';
import {AtomTitle} from './atomTitle';
import {StyledParameterLineDiv, StyledParameterTitleLineDiv} from './parameterLine';
import {AtomParameterPicker} from './pickers/atomParameterPicker';

export const FormatDateAtomCard: FC<AtomCardProps> = (props) => {
  const theme = useUserTheme();
  const {atom, isDisabled, onUpdateAtom, onCancel} = props;
  const [formatParam, abbreviatedParam, timezoneParam, localeParam] = getParameters(atom);

  // Auto-expand if params have been edited.
  const shouldExpandCollapsableParameters = !isEqual(atom.params, getAtomDefaultParameters(atom));

  const collapsableParameters = useMemo(
    () => [
      <StyledParameterLineDiv key="abbreviated">
        <AtomParameterPicker
          parameter={abbreviatedParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      </StyledParameterLineDiv>,
      <StyledParameterLineDiv key="timezone">
        <StyledParameterTitleLineDiv>
          <Trans id="rule-expressions-timezone">Time zone</Trans>
        </StyledParameterTitleLineDiv>
        <AtomParameterPicker
          parameter={timezoneParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      </StyledParameterLineDiv>,
      <StyledParameterLineDiv key="locale">
        <StyledParameterTitleLineDiv>
          <span>
            <Trans id="rule-expressions-locale">Locale</Trans>
          </span>
          <TooltipCoordinator
            iconColor={theme.greys.shade50}
            render={() => (
              <Trans id="expressions-param-locale-toolip">
                Format according to the provided language conventions.
              </Trans>
            )}
          >
            <TooltipIconWithHover />
          </TooltipCoordinator>
        </StyledParameterTitleLineDiv>
        <AtomParameterPicker
          parameter={localeParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      </StyledParameterLineDiv>,
    ],
    [timezoneParam, localeParam],
  );

  const title = (
    <AtomTitle atom={atom} title={<Trans id="expression-formatas">Format as</Trans>}>
      <AtomParameterPicker
        parameter={formatParam}
        atom={atom}
        onUpdate={onUpdateAtom}
        isDisabled={isDisabled}
      />
    </AtomTitle>
  );

  return (
    <RuleMultilineParameterCard
      errorMessage={atom.errorMessage}
      icon={<AtomIcon atom={atom} />}
      title={title}
      onCancel={onCancel}
      collapsableParameters={collapsableParameters}
      shouldExpandCollapsableParameters={shouldExpandCollapsableParameters}
    />
  );
};
